import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import * as React from "react";
import {
  confirmResetPassword,
  getCurrentUser,
  resetPassword,
} from "@aws-amplify/auth";

import { authErrorMessage, useAuth } from "../auth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

const postAuthFallback = "/" as const;

export const Route = createFileRoute("/forgot-password")({
  beforeLoad: async () => {
    let isAuthenticated = false;
    try {
      isAuthenticated = !!(await getCurrentUser());
    } catch {
      /* no session */
    }
    if (isAuthenticated) {
      throw redirect({ to: postAuthFallback });
    }
  },
  component: ForgotPasswordComponent,
});

function ForgotPasswordComponent() {
  const auth = useAuth();
  const router = useRouter();
  const navigate = useNavigate();
  const [username, setUsername] = React.useState("");
  const [code, setCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [step, setStep] = React.useState<"request" | "confirm">("request");

  const handleRequestReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);
    try {
      await resetPassword({ username });
      setSuccessMessage("Password reset code sent! Please check your email.");
      setStep("confirm");
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      setIsLoading(false);
      return;
    }

    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword,
      });
      setSuccessMessage("Password updated! Signing you in…");
      const loginResult = await auth.login(username, newPassword);
      if (loginResult.phase === "signed_in") {
        await router.invalidate();
        await navigate({ to: postAuthFallback, replace: true });
        return;
      }
      if (loginResult.phase === "new_password_required") {
        await router.invalidate();
        await navigate({ to: "/login", replace: true });
        return;
      }
    } catch (err) {
      setSuccessMessage(null);
      setError(
        authErrorMessage(err) ||
          "Something went wrong. If your password was updated, try signing in."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (step === "confirm") {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-sm">
          <Card>
            <CardHeader>
              <CardTitle>Reset your password</CardTitle>
              <CardDescription>
                Enter the code sent to your email and your new password
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConfirmReset}>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="code">Verification code</FieldLabel>
                    <Input
                      id="code"
                      type="text"
                      placeholder="Enter verification code"
                      value={code}
                      onChange={(e) =>
                        setCode(e.target.value.replace(/\D/g, ""))
                      }
                      required
                      maxLength={6}
                      autoComplete="one-time-code"
                      disabled={isLoading}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="newPassword">New password</FieldLabel>
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="Enter your new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      disabled={isLoading}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="confirmPassword">
                      Confirm new password
                    </FieldLabel>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      disabled={isLoading}
                    />
                  </Field>
                  {error ? (
                    <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                      {error}
                    </div>
                  ) : null}
                  {successMessage ? (
                    <div className="rounded-md bg-green-500/10 p-3 text-sm text-green-600 dark:text-green-400">
                      {successMessage}
                    </div>
                  ) : null}
                  <Field>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading || code.length < 6}
                    >
                      {isLoading ? "Resetting…" : "Reset password"}
                    </Button>
                  </Field>
                  <div className="text-center text-sm">
                    <button
                      type="button"
                      className="text-primary font-medium underline-offset-4 hover:underline"
                      onClick={() => {
                        setStep("request");
                        setCode("");
                        setNewPassword("");
                        setConfirmPassword("");
                        setError(null);
                        setSuccessMessage(null);
                      }}
                    >
                      Back to request code
                    </button>
                  </div>
                </FieldGroup>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <CardTitle>Forgot password</CardTitle>
            <CardDescription>
              Enter your email to receive a password reset code
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRequestReset}>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="username">Email</FieldLabel>
                  <Input
                    id="username"
                    type="email"
                    placeholder="you@example.com"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </Field>
                {error ? (
                  <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                  </div>
                ) : null}
                {successMessage ? (
                  <div className="rounded-md bg-green-500/10 p-3 text-sm text-green-600 dark:text-green-400">
                    {successMessage}
                  </div>
                ) : null}
                <Field>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? "Sending…" : "Send reset code"}
                  </Button>
                </Field>
                <div className="text-center text-sm text-muted-foreground">
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    className="text-primary font-medium underline-offset-4 hover:underline"
                  >
                    Log in
                  </Link>
                </div>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
